import { useState, type ReactElement } from 'react'

import { ChevronRight } from 'lucide-react'

import type { FilteringRuleRead, RuleExamplesRead } from '@/client'
import { useFilteringRules } from '@/components/features/use-filtering-rules'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'

function predicateSummary(predicate: FilteringRuleRead['predicate']): string {
  if (predicate.kind === 'video') {
    return predicate.video_scope === 'provider'
      ? 'All YouTube videos'
      : `YouTube video ${predicate.video_id ?? ''}`.trim()
  }
  const subdomains = predicate.include_subdomains ? ' and its subdomains' : ''
  const mode = predicate.path_mode === 'exact' ? ' (exact path)' : ''
  return `${predicate.host}${predicate.path}${subdomains}${mode}`
}

function ActionBadge({ rule }: { rule: FilteringRuleRead }) {
  return <Badge variant={rule.action === 'block' ? 'destructive' : 'secondary'}>{rule.action}</Badge>
}

function RulePredicateDetails({ predicate }: { predicate: FilteringRuleRead['predicate'] }) {
  if (predicate.kind === 'video') {
    return (
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Provider</dt>
        <dd className="font-mono">{predicate.provider}</dd>
        <dt className="text-muted-foreground">Video scope</dt>
        <dd>{predicate.video_scope === 'provider' ? 'Provider-wide' : 'Specific video'}</dd>
        {predicate.video_id !== null && (
          <>
            <dt className="text-muted-foreground">Video ID</dt>
            <dd className="font-mono">{predicate.video_id}</dd>
          </>
        )}
        {predicate.source_url !== null && (
          <>
            <dt className="text-muted-foreground">Source URL</dt>
            <dd className="break-all font-mono">{predicate.source_url}</dd>
          </>
        )}
      </dl>
    )
  }
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-muted-foreground">Host</dt>
      <dd className="font-mono">{predicate.host}</dd>
      <dt className="text-muted-foreground">Subdomains</dt>
      <dd>{predicate.include_subdomains ? 'Included' : 'Named host only'}</dd>
      <dt className="text-muted-foreground">Path</dt>
      <dd className="font-mono">{predicate.path}</dd>
      <dt className="text-muted-foreground">Path mode</dt>
      <dd className="capitalize">{predicate.path_mode}</dd>
    </dl>
  )
}

function ExampleList({ title, examples }: { title: string; examples: string[] }) {
  if (examples.length === 0) {
    return null
  }
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{title}</p>
      <ul className="space-y-1">
        {examples.map((example) => (
          <li key={example} className="break-all font-mono text-xs">
            {example}
          </li>
        ))}
      </ul>
    </div>
  )
}

function RuleExamples({ title, examples }: { title: string; examples: RuleExamplesRead }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide">{title}</p>
      <div className="grid gap-3">
        <ExampleList title="Should match" examples={examples.should_match} />
        <ExampleList title="Should not match" examples={examples.should_not_match} />
      </div>
    </div>
  )
}

function DeleteRuleAction({
  rule,
  trigger,
  pending,
  onDelete,
}: {
  rule: FilteringRuleRead
  trigger: string
  pending: boolean
  onDelete: (ruleId: string) => void
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="destructive" disabled={pending}>
          {trigger}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
          <AlertDialogDescription>
            {rule.description} stops applying immediately. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onDelete(rule.id)}>Delete rule</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function RuleGroup({
  title,
  empty,
  rules,
  pending,
  onOpen,
}: {
  title: string
  empty: string
  rules: FilteringRuleRead[]
  pending: boolean
  onOpen: (ruleId: string) => void
}) {
  const headingId = `filtering-rules-${title.toLowerCase().replace(/\s+/, '-')}-heading`
  return (
    <div className="space-y-1.5">
      <h3 id={headingId} className="text-muted-foreground px-1 text-xs font-semibold uppercase tracking-wide">
        {title}
      </h3>
      {rules.length === 0 ? (
        <ul aria-labelledby={headingId} className="divide-border divide-y overflow-hidden rounded-lg border">
          <li className="text-muted-foreground p-3 text-sm">{empty}</li>
        </ul>
      ) : (
        <ul aria-labelledby={headingId} className="divide-border divide-y overflow-hidden rounded-lg border">
          {rules.map((rule) => (
            <li key={rule.id}>
              <button
                type="button"
                disabled={pending}
                aria-label={rule.description}
                onClick={() => onOpen(rule.id)}
                className="hover:bg-accent flex w-full items-center justify-between gap-3 p-3 text-left active:opacity-70 [-webkit-tap-highlight-color:transparent]"
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-2">
                    <ActionBadge rule={rule} />
                    {rule.status === 'proposed' && <Badge variant="outline">Proposal</Badge>}
                  </span>
                  <span className="text-muted-foreground mt-1 block truncate text-sm">
                    {predicateSummary(rule.predicate)}
                  </span>
                </span>
                <ChevronRight aria-hidden="true" className="text-muted-foreground size-5 shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function ParentRules({ deviceId }: { deviceId: string }): ReactElement {
  const {
    rules,
    rulesError,
    proposedRules,
    enabledRules,
    disabledRules,
    generatePending,
    form,
    clarificationForm,
    originalIntent,
    clarification,
    clarificationAnswers,
    submitIntent,
    submitClarification,
    restartClarification,
    replacementTarget,
    startReplacement,
    cancelReplacement,
    existingRule,
    resetAuthoring,
    generatedProposal,
    conflict,
    conflictingRule,
    requestedRule,
    conflictCanSwap,
    conflictDialogOpen,
    setConflictDialogOpen,
    confirmRule,
    deleteRule,
    setRuleEnabled,
    setEnabledPending,
    mutationPending,
    mutationFailed,
  } = useFilteringRules({ source: 'parent', deviceId })
  const [newRuleOpen, setNewRuleOpen] = useState(false)
  const [activeRuleId, setActiveRuleId] = useState<string | null>(null)
  const activeRule = rules.find((rule) => rule.id === activeRuleId) ?? null

  const closeNewRule = (open: boolean): void => {
    setNewRuleOpen(open)
    if (!open) {
      resetAuthoring()
    }
  }

  const confirmGenerated = (): void => {
    if (generatedProposal !== null) {
      confirmRule(generatedProposal.id, () => closeNewRule(false))
    }
  }

  const discardGenerated = (): void => {
    if (generatedProposal !== null) {
      deleteRule(generatedProposal.id, () => closeNewRule(false))
    }
  }

  return (
    <section aria-labelledby="parent-filtering-rules-heading" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 id="parent-filtering-rules-heading" className="text-lg font-semibold">
          Filtering rules
        </h2>
        <Button type="button" size="sm" onClick={() => setNewRuleOpen(true)}>
          + New rule
        </Button>
      </div>

      {mutationFailed && (
        <p role="alert" className="text-destructive text-sm">
          The Filtering rule request failed. Review the intent and try again.
        </p>
      )}
      {rulesError && (
        <p role="alert" className="text-destructive text-sm">
          Filtering rules could not be loaded.
        </p>
      )}

      <RuleGroup
        title="Rule proposals"
        empty="No proposals."
        rules={proposedRules}
        pending={mutationPending}
        onOpen={setActiveRuleId}
      />
      <RuleGroup
        title="Enabled"
        empty="No enabled rules."
        rules={enabledRules}
        pending={mutationPending}
        onOpen={setActiveRuleId}
      />
      <RuleGroup
        title="Disabled"
        empty="No disabled rules."
        rules={disabledRules}
        pending={mutationPending}
        onOpen={setActiveRuleId}
      />

      {conflictCanSwap && conflict && conflictingRule && requestedRule && (
        <AlertDialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Replace currently enabled rule?</AlertDialogTitle>
              <AlertDialogDescription>
                Enabling {requestedRule.description} will disable {conflictingRule.description}.
                Both changes happen together.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setRuleEnabled(requestedRule.id, true, conflict.conflicting_rule_id)
                }}
              >
                Swap enabled rule
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {conflict && !conflictCanSwap && (
        <p role="alert" className="text-destructive text-sm">
          Cannot enable this rule because {conflictingRule?.description ?? 'another rule'} (
          {conflict.conflicting_rule_id}) is already enabled. No rules were changed.
        </p>
      )}

      <Drawer open={newRuleOpen} onOpenChange={closeNewRule}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{replacementTarget === null ? 'New rule' : 'Replace rule'}</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 overflow-y-auto p-4 pt-0">
            {mutationFailed && (
              <p role="alert" className="text-destructive text-sm">
                The Filtering rule request failed. Review the intent and try again.
              </p>
            )}
            {replacementTarget !== null && (
              <div role="status" className="bg-muted space-y-2 rounded-md p-3 text-sm">
                <p>
                  Replacing <span className="font-medium">{replacementTarget.description}</span>.
                  Generate and review the new meaning before confirming.
                </p>
                <Button type="button" variant="outline" onClick={cancelReplacement}>
                  Cancel replacement
                </Button>
              </div>
            )}

            {generatedProposal !== null ? (
              <div className="space-y-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <ActionBadge rule={generatedProposal} />
                    <Badge variant="outline">Proposal</Badge>
                  </div>
                  <p className="text-base leading-6">{generatedProposal.description}</p>
                </div>
                <RulePredicateDetails predicate={generatedProposal.predicate} />
                {generatedProposal.replaces_rule_id !== null && (
                  <p className="text-muted-foreground text-sm">
                    Replaces{' '}
                    {
                      rules.find((rule) => rule.id === generatedProposal.replaces_rule_id)
                        ?.description
                    }
                    .
                  </p>
                )}
                <RuleExamples title="Model examples" examples={generatedProposal.model_examples} />
                <RuleExamples
                  title="Server boundary examples"
                  examples={generatedProposal.server_examples}
                />
              </div>
            ) : existingRule !== null ? (
              <div role="status" className="space-y-3 text-sm">
                <p>
                  <span className="font-medium">{existingRule.rule.description}</span>{' '}
                  {existingRule.recommended_action === 'none'
                    ? 'This rule is already enabled. No changes are needed.'
                    : 'This rule already exists but is disabled.'}
                </p>
                {existingRule.recommended_action === 'enable' && (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={setEnabledPending}
                    onClick={() => {
                      setRuleEnabled(existingRule.rule.id, true)
                      closeNewRule(false)
                    }}
                  >
                    Enable existing rule
                  </Button>
                )}
              </div>
            ) : originalIntent !== null ? (
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">Original intent</p>
                  <p className="text-sm">{originalIntent}</p>
                </div>
                {clarificationAnswers.length > 0 && (
                  <ol className="space-y-2">
                    {clarificationAnswers.map((answer, index) => (
                      <li
                        key={`${index}-${answer.question}`}
                        className="bg-muted rounded-md p-3 text-sm"
                      >
                        <p className="font-medium">
                          Question {index + 1}: {answer.question}
                        </p>
                        <p>Answer: {answer.answer}</p>
                      </li>
                    ))}
                  </ol>
                )}
                {clarification !== null && (
                  <div className="space-y-3">
                    <p className="font-medium">{clarification.question}</p>
                    <div className="flex flex-wrap gap-2">
                      {clarification.choices.map((choice) => (
                        <Button
                          key={choice}
                          type="button"
                          variant="outline"
                          disabled={generatePending}
                          onClick={() => submitClarification(choice)}
                        >
                          {choice}
                        </Button>
                      ))}
                    </div>
                    <Form {...clarificationForm}>
                      <form
                        onSubmit={clarificationForm.handleSubmit((values) =>
                          submitClarification(values.answer),
                        )}
                        className="space-y-3"
                      >
                        <FormField
                          control={clarificationForm.control}
                          name="answer"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Your answer</FormLabel>
                              <FormControl>
                                <Textarea {...field} rows={2} maxLength={500} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button type="submit" disabled={generatePending}>
                          Submit answer
                        </Button>
                      </form>
                    </Form>
                  </div>
                )}
                {generatePending && (
                  <p role="status" className="text-muted-foreground text-sm">
                    Generating the next step…
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  disabled={generatePending}
                  onClick={restartClarification}
                >
                  Start over
                </Button>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(submitIntent)} className="space-y-3">
                  <FormField
                    control={form.control}
                    name="intent_text"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Filtering intent</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Block YouTube Shorts on this device"
                            rows={3}
                            maxLength={500}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" disabled={generatePending} className="w-full">
                    {generatePending ? 'Generating…' : 'Generate proposal'}
                  </Button>
                </form>
              </Form>
            )}
          </div>
          {generatedProposal !== null && (
            <DrawerFooter className="flex-row gap-2">
              <Button
                type="button"
                className="flex-1"
                disabled={mutationPending}
                onClick={confirmGenerated}
              >
                Confirm proposal
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={mutationPending}
                onClick={discardGenerated}
              >
                Discard proposal
              </Button>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      <Drawer
        open={activeRule !== null}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setActiveRuleId(null)
          }
        }}
      >
        <DrawerContent>
          {activeRule !== null && (
            <>
              <DrawerHeader>
                <DrawerTitle>{activeRule.description}</DrawerTitle>
                <div className="flex items-center gap-2 pt-1">
                  <ActionBadge rule={activeRule} />
                  <Badge variant="outline" className="capitalize">
                    {activeRule.status}
                  </Badge>
                </div>
              </DrawerHeader>
              <div className="space-y-4 overflow-y-auto p-4 pt-0">
                <RulePredicateDetails predicate={activeRule.predicate} />
                <RuleExamples title="Model examples" examples={activeRule.model_examples} />
                <RuleExamples
                  title="Server boundary examples"
                  examples={activeRule.server_examples}
                />
                {activeRule.replaces_rule_id !== null && (
                  <p className="text-muted-foreground text-sm">
                    Replaced{' '}
                    {rules.find((rule) => rule.id === activeRule.replaces_rule_id)?.description}.
                  </p>
                )}
              </div>
              <DrawerFooter className="flex-row flex-wrap gap-2">
                {activeRule.status === 'proposed' ? (
                  <>
                    <Button
                      type="button"
                      className="flex-1"
                      disabled={mutationPending}
                      onClick={() => {
                        confirmRule(activeRule.id)
                        setActiveRuleId(null)
                      }}
                    >
                      Confirm proposal
                    </Button>
                    <DeleteRuleAction
                      rule={activeRule}
                      trigger="Discard proposal"
                      pending={mutationPending}
                      onDelete={(ruleId) => {
                        deleteRule(ruleId)
                        setActiveRuleId(null)
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      disabled={mutationPending}
                      onClick={() => {
                        setRuleEnabled(activeRule.id, activeRule.status === 'disabled')
                        setActiveRuleId(null)
                      }}
                    >
                      {activeRule.status === 'enabled' ? 'Disable rule' : 'Enable rule'}
                    </Button>
                    {activeRule.status === 'enabled' && (
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        disabled={mutationPending}
                        onClick={() => {
                          startReplacement(activeRule)
                          setActiveRuleId(null)
                          setNewRuleOpen(true)
                        }}
                      >
                        Replace rule
                      </Button>
                    )}
                    <DeleteRuleAction
                      rule={activeRule}
                      trigger="Delete rule"
                      pending={mutationPending}
                      onDelete={(ruleId) => {
                        deleteRule(ruleId)
                        setActiveRuleId(null)
                      }}
                    />
                  </>
                )}
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </section>
  )
}
